<script lang="ts">
	import type { Device } from 'src/types/Device';
	import { format } from 'src/services/i18n';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { getContext, onDestroy, onMount } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	import { Button, Select, Text } from '@Shelly/shelly-design-system';
	import {
		getCameraCapabilities,
		getCameraConfig,
		saveCameraConfig,
	} from '../../helpers/camera-settings-helpers';
	import type { CameraStreamConfig } from '../../types/camera-settings';

	export let device: Device;

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	const cloneStreams = (
		src: Record<string, CameraStreamConfig>,
	): Record<string, CameraStreamConfig> => {
		const out: Record<string, CameraStreamConfig> = {};
		for (const [k, v] of Object.entries(src)) {
			out[k] = { ...v };
		}
		return out;
	};

	const readStreams = (): Record<string, CameraStreamConfig> => {
		const raw = getCameraConfig(device)?.streams;
		if (!raw || typeof raw !== 'object') return {};
		const out: Record<string, CameraStreamConfig> = {};
		for (const [k, v] of Object.entries(raw as Record<string, any>).sort(
			([a], [b]) => Number(a) - Number(b),
		)) {
			out[k] = {
				bitrate: typeof v?.bitrate === 'number' ? v.bitrate : 512,
				resolution: typeof v?.resolution === 'string' ? v.resolution : '640x360@10',
			};
		}
		return out;
	};

	let streams = readStreams();
	let cache = cloneStreams(streams);
	let loading = false;
	let resolutionOptions = Object.values(streams).map((s) => ({
		title: s.resolution,
		value: s.resolution,
	}));
	let bitrateOptions = Object.values(streams).map((s) => ({
		title: `${s.bitrate} kbps`,
		value: s.bitrate,
	}));

	$: streamKeys = Object.keys(streams).sort((a, b) => Number(a) - Number(b));

	$: hasChanges = streamKeys.some(
		(k) =>
			streams[k]?.resolution !== cache[k]?.resolution || streams[k]?.bitrate !== cache[k]?.bitrate,
	);

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({
				onClick: save,
				loading,
				disabled: !hasChanges || loading,
				secondaryButton: {
					onClick: resetToDefaults,
					label: $format('button_reset_to_defaults', { default: 'Reset to defaults' }),
					variant: 'secondary',
				},
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	const getSelectValue = (e: CustomEvent): any => {
		const d = e.detail;
		if (typeof d === 'string' || typeof d === 'number') return d;
		return d?.value ?? d;
	};

	const streamLabel = (key: string): string => {
		return key === '0'
			? $format('camera_stream_main', { default: 'Main stream' })
			: $format('camera_stream_n', { values: { n: key }, default: `Stream #${key}` });
	};

	const save = async () => {
		if (!hasChanges) return;
		loading = true;
		const success = await saveCameraConfig(
			device,
			{ streams: cloneStreams(streams) },
			$format('setting_scc_saved_toast', { default: 'Setting saved successfully.' }),
		);
		if (success) {
			cache = cloneStreams(streams);
		} else {
			streams = cloneStreams(cache);
		}
		loading = false;
	};

	const resetToDefaults = async () => {
		loading = true;
		const success = await saveCameraConfig(
			device,
			{ streams: null },
			$format('camera_streams_reset_success', { default: 'Streams reset to defaults.' }),
		);
		if (success) {
			streams = readStreams();
			cache = cloneStreams(streams);
		}
		loading = false;
	};

	onMount(async () => {
		const caps = await getCameraCapabilities(device);
		if (caps) {
			resolutionOptions = caps.resolutions.map((r) => ({ title: r, value: r }));
			const [min, max] = caps.bitrate_range;
			const step = min || 512;
			const steps: number[] = [];
			for (let b = min; b <= max; b += step) steps.push(b);
			if (steps[steps.length - 1] !== max) steps.push(max);
			bitrateOptions = steps.map((b) => ({ title: `${b} kbps`, value: b }));
		}
	});

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

{#each streamKeys as key (key)}
	<div class="mb-m">
		<Text styleType="h2" class="mb-s">
			{streamLabel(key)}
		</Text>
		<Select
			size="m"
			width="full"
			icon="fas fa-display"
			label={$format('camera_stream_resolution', { default: 'Resolution' })}
			options={resolutionOptions}
			value={resolutionOptions.find((o) => o.value === streams[key]?.resolution)}
			on:change={(e) => {
				streams[key] = { ...streams[key], resolution: getSelectValue(e) };
				streams = streams;
			}}
			id="camera-stream-res-{key}"
			aria-label={$format('camera_stream_resolution', { default: 'Resolution' })}
		/>
		<Select
			size="m"
			width="full"
			class="mt-s"
			icon="fas fa-gauge-high"
			label={$format('camera_stream_bitrate', { default: 'Bitrate' })}
			options={bitrateOptions}
			value={bitrateOptions.find((o) => o.value === streams[key]?.bitrate)}
			on:change={(e) => {
				streams[key] = { ...streams[key], bitrate: getSelectValue(e) };
				streams = streams;
			}}
			id="camera-stream-bitrate-{key}"
			aria-label={$format('camera_stream_bitrate', { default: 'Bitrate' })}
		/>
	</div>
{/each}

{#if !$showNewLayout}
	<Button
		class="mb-s"
		variant="secondary"
		width="full"
		size="m"
		disabled={loading}
		on:click={resetToDefaults}
	>
		{$format('button_reset_to_defaults', { default: 'Reset to defaults' })}
	</Button>
	<Button width="full" size="m" {loading} disabled={!hasChanges || loading} on:click={save}>
		{$format('button_save', { default: 'Save' })}
	</Button>
{/if}
